import { useEffect, useMemo, useState } from "react";
import { Icon } from "../Icon";
import { Card, Pill } from "../ui";
import type { PuzzleInstance } from "../../puzzles/core/framework";
import type { SpecPuzzleTemplateId } from "../../puzzles/core/specTemplates";
import type { ValidationResult } from "../../puzzles/core/types";
import { SimulationControls } from "./renderers/shared";
import { SpecPuzzleRenderer } from "./renderers/SpecPuzzleRenderers";

export function GenericPuzzlePlayer({
  templateId,
  instance,
  feedback,
  visualConfig,
  onLock,
}: {
  templateId: SpecPuzzleTemplateId;
  instance: PuzzleInstance;
  feedback?: ValidationResult | null;
  visualConfig?: Record<string, unknown>;
  onLock(submission: Record<string, unknown>): void;
}) {
  const initialAnswers = useMemo(
    () =>
      Object.fromEntries(
        instance.expectedAnswer.fields.map((field) => [
          field.key,
          field.type === "boolean" ? false : "",
        ]),
      ),
    [instance],
  );
  const [answers, setAnswers] = useState<Record<string, unknown>>(initialAnswers);
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    setAnswers(initialAnswers);
  }, [initialAnswers]);

  const lock = () => {
    if (simulating) return;
    setSimulating(true);
    window.setTimeout(() => {
      setSimulating(false);
      onLock(answers);
    }, 650);
  };

  return (
    <div className="grid gap-5 xl:grid-cols-[.58fr_1.42fr]">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-xl font-bold text-white">
            Puzzle response
          </h3>
          <Pill tone="cyan">{kindLabel(instance.visual.kind)}</Pill>
        </div>
        <p className="mt-3 text-sm leading-6 text-slate-300">{instance.prompt}</p>
        {instance.expectedAnswer.instructions && (
          <p className="mt-3 text-xs leading-5 text-slate-500">
            {instance.expectedAnswer.instructions}
          </p>
        )}
        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950/55 p-4">
          <div className="flex items-center gap-3 text-slate-300">
            <span className="rounded-xl bg-cyan-400/10 p-2 text-cyan-300">
              <Icon name="ScanSearch" size={20} />
            </span>
            <div>
              <p className="font-display text-base font-bold text-white">
                Interactive template
              </p>
              <p className="text-xs text-slate-500">
                {templateLabel(templateId)}
              </p>
            </div>
          </div>
          <dl className="mt-4 grid gap-3 text-xs text-slate-400">
            {instance.expectedAnswer.fields.map((field) => (
              <div key={field.key} className="flex items-center justify-between gap-3">
                <dt>{field.label}</dt>
                <dd className="font-mono text-slate-200">
                  {formatAnswerPreview(answers[field.key])}
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="mt-6">
          <SimulationControls running={simulating} onSubmit={lock} />
        </div>
      </Card>

      <Card className="overflow-hidden p-5">
        <div className="mb-4 flex items-center justify-between">
          <span className="font-retro text-[8px] uppercase tracking-widest text-slate-400">
            TEMPLATE INSTANCE
          </span>
          <span className="flex items-center gap-2 text-[10px] font-bold text-emerald-300">
            <span
              className={`h-2 w-2 rounded-full ${
                simulating ? "animate-pulse-soft bg-amber-300" : "bg-emerald-300"
              }`}
            />
            {simulating ? "RUNNING" : "READY"}
          </span>
        </div>
        <SpecPuzzleRenderer
          templateId={templateId}
          instance={instance}
          answer={answers}
          feedback={feedback}
          visualConfig={visualConfig}
          disabled={simulating}
          running={simulating}
          onAnswerChange={setAnswers}
        />
      </Card>
    </div>
  );
}

function kindLabel(kind: PuzzleInstance["visual"]["kind"]) {
  return kind
    .split("-")
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

function templateLabel(templateId: string) {
  return templateId
    .split("-")
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

function formatAnswerPreview(value: unknown) {
  if (value === true) return "yes";
  if (value === false) return "no";
  if (value === "" || value == null) return "not set";
  return String(value);
}
